import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, LayoutDashboard } from 'lucide-react';
import { Navbar1 } from '../components/shadcnblocks-com-navbar1';
import FondoDeGranos, {
  FONDO_ANCHO,
  FONDO_ANGOSTO,
  RESPALDO_LANDING,
  usePantallaAncha,
} from '../components/business/FondoDeGranos';
import { Footer } from '../components/ui/footer';
import { Button } from '../components/ui/button';
import { footerData } from '../data/navegacion';
import { motion } from 'framer-motion';
import './DemoPage.css';

const DemoPage = () => {
  const navigate = useNavigate();
  const pantallaAncha = usePantallaAncha();

  // Configuración del navbar para Aturno
  const navbarConfig = {
    logo: {
      url: "/",
      src: "",
      alt: "Aturno Logo",
      title: "Aturno",
    },
    menu: [
      // Anclas de la landing con la barra adelante, para que funcionen también
      // desde esta página. Antes esto apuntaba a /caracteristicas, /ayuda,
      // /configuracion, /precios y /acerca-de, ninguna de las cuales existe
      // en App.js: el catch-all /:slug las convertía en páginas rotas.
      { title: "Inicio", url: "/" },
      { title: "Funcionalidades", url: "/#caracteristicas" },
      { title: "Planes", url: "/#precios" },
      { title: "Preguntas", url: "/#recursos" },
      { title: "Demo", url: "/demo" },
    ],
    mobileExtraLinks: [
      // Solo rutas que existen en App.js. Antes esto apuntaba a
      // /contacto, /soporte, /blog y /status, que no existen y que el
      // catch-all /:slug convertía en páginas de negocio rotas.
      { name: "Ver demo", url: "/demo" },
      { name: "Crear cuenta", url: "/auth/register" },
    ],
    auth: {
      login: { text: "Iniciar Sesión", url: "/auth/login" },
      signup: { text: "Registrarse", url: "/auth/register" },
    },
  };

  return (
    /* EL FONDO ES EL MISMO QUE EL DE LA LANDING Y EL DE AUTH.
       Acá había un `AuroraBackground`: el degradado animado viejo, que ya no
       usa ninguna otra pantalla —la landing y las tres de auth pasaron a
       `FondoDeGranos`—, así que /demo era la única que seguía con el fondo
       anterior y se leía como de otro producto. Se monta igual que en
       LandingDos: absoluto, detrás de todo y alto una pantalla, porque el
       shader está medido para la primera vista y no para el largo del scroll. */
    <div className="demo-page-new">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[100svh]"
      >
        <FondoDeGranos
          posicion={pantallaAncha ? FONDO_ANCHO : FONDO_ANGOSTO}
          respaldo={RESPALDO_LANDING}
        />
      </div>

      {/* Navbar de shadcn/ui */}
      <Navbar1 {...navbarConfig} />

      {/* Demo Options Section */}
      <section className="demo-options-section">
        <div className="demo-options-container">
          <div className="demo-options-grid">
            {/* SIN `whileHover`. Las dos tarjetas se levantaban 5px al pasar el
                mouse; era el único elemento de la app que lo hacía y, con la
                sombra creciendo al mismo tiempo, la tarjeta parecía despegarse
                de la página. La entrada (opacity + y) se queda: ésa ocurre una
                vez al cargar, no cada vez que el mouse cruza. */}
            <motion.div
              className="demo-option-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="demo-card-header">
                <div className="demo-card-icon client-icon">
                  <Calendar size={48} />
                </div>
                <h2>Perspectiva del Cliente</h2>
                <p>Experimenta cómo es sacar un turno en un negocio que usa Aturno. Proceso simple y rápido.</p>
              </div>
              {/* El <Button> de la app, no un botón propio. Los dos de acá eran
                  `.demo-btn` con su degradado escrito a mano —naranja el de
                  cliente, violeta el de negocio— y su propio alto, radio y
                  hover: tres botones distintos de los de la landing en la misma
                  sesión. `size="cta"` es el mismo que usa la landing. */}
              <Button
                size="cta"
                className="demo-card-accion w-full"
                onClick={() => navigate('/demo/business')}
              >
                Ver como Cliente
              </Button>
            </motion.div>

            <motion.div
              className="demo-option-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <div className="demo-card-header">
                <div className="demo-card-icon business-icon">
                  <LayoutDashboard size={48} />
                </div>
                <h2>Panel de Negocio</h2>
                <p>Descubre cómo gestionar tus turnos, clientes y servicios desde el dashboard profesional.</p>
              </div>
              <Button
                size="cta"
                className="demo-card-accion w-full"
                onClick={() => navigate('/demo/dashboard')}
              >
                Ver Dashboard
              </Button>
            </motion.div>
          </div>

          <motion.div
            className="demo-note"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
          >
            <div className="demo-note-content">
              <div className="demo-note-icon">💡</div>
              <div>
                <h3>Demostración Interactiva</h3>
                <p>Todos los datos son simulados para fines demostrativos. No se guarda información real.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SIN CTA PROPIO ACÁ.
          Esta página cerraba con "¿Te gustó lo que viste?" + "Comenzar Gratis" /
          "Volver al Inicio", y justo abajo el Footer abre con "Hacé crecer tu
          negocio con Aturno" y su propio botón de registro: dos cierres pegados
          pidiendo lo mismo. El del footer sale de data/navegacion.js y aparece
          en las tres páginas públicas, así que el que sobraba era éste. */}
      {/* Footer */}
      <Footer {...footerData} />
    </div>
  );
};

export default DemoPage;
